import Phaser from 'phaser';
import { TEX, CLASS_COLORS } from '@/data/assetManifest';
import {
  NPC_SPRITE_ENTRIES,
  NPC_SPRITE_FRAME_H,
  NPC_SPRITE_FRAME_W,
} from '@/data/npcSpriteRegistry';
import {
  MONSTER_SPRITE_ENTRIES,
  MONSTER_MOVE_FRAME_W,
  MONSTER_MOVE_FRAME_H,
  MONSTER_DIE_FRAME_W,
  MONSTER_DIE_FRAME_H,
  monsterDirRow,
  type MonsterDir,
} from '@/data/monsterSpriteRegistry';
import { AudioManager } from '@/systems/AudioManager';
import itemsData from '@/data/items.json';

// Real-asset PreloadScene — loads PNGs from /assets/downloads/* and defines
// 4-direction walk animations from hero.png / npc.png (3 cols x 4 rows, 16x16).
// Only a few small FX textures and procedural fallbacks remain generated.
export class PreloadScene extends Phaser.Scene {
  constructor() { super('Preload'); }

  preload() {
    // Loading bar
    const bar = this.add.rectangle(640, 360, 400, 14, 0x222a36).setOrigin(0.5);
    const fill = this.add.rectangle(440, 360, 0, 10, 0x4cd1ff).setOrigin(0, 0.5);
    const text = this.add.text(640, 330, 'Loading assets...', { fontFamily: '"Philosopher", "Cinzel", serif', color: '#cfd8dc', fontSize: '16px' }).setOrigin(0.5);
    this.load.on('progress', (p: number) => { fill.width = 400 * p; });
    this.load.on('complete', () => { bar.destroy(); fill.destroy(); text.destroy(); });

    const base = '/assets/downloads';

    // Tile atlases (16x16 frames)
    this.load.spritesheet(TEX.tinyTiles, `${base}/tilesets/tiny16_basictiles.png`, { frameWidth: 16, frameHeight: 16 });
    this.load.spritesheet(TEX.tinyThings, `${base}/sprites/tiny16_things.png`, { frameWidth: 16, frameHeight: 16 });
    this.load.spritesheet(TEX.tinyChars, `${base}/sprites/tiny16_characters.png`, { frameWidth: 16, frameHeight: 16 });

    // Sample/decorative bigger tile sets
    this.load.image(TEX.tinyDungeon, `${base}/tilesets/tiny16_dungeon.png`);
    this.load.image(TEX.tinyOverworld, `${base}/tilesets/tiny16_overworld.png`);
    this.load.image(TEX.dungeonBigTiles, `${base}/tilesets/dungeon_tiles.png`);
    this.load.image(TEX.ttdDungeon, `${base}/tilesets/top_down_dungeon_and_character/dungeon.png`);

    // Character select screen assets (MU Online style)
    this.load.image(TEX.csBg, '/assets/ui/charselect/bg_charselect.png');
    this.load.image(TEX.csWarrior, '/assets/ui/charselect/portrait_warrior.png');
    this.load.image(TEX.csWizard, '/assets/ui/charselect/portrait_wizard.png');
    this.load.image(TEX.csArcher, '/assets/ui/charselect/portrait_archer.png');

    // Welcome / Login screen MU Online cinematic key art
    this.load.image(TEX.loginBg, '/assets/ui/login/login_bg_hero.jpg');
    this.load.image(TEX.loginLogo, '/assets/ui/login/login_logo.png');
    this.load.image(TEX.loginWarrior, '/assets/ui/login/login_warrior.png');
    this.load.image(TEX.loginWizard, '/assets/ui/login/login_wizard.png');
    this.load.image(TEX.loginPanel, '/assets/ui/login/login_panel.png');

    // Town RPG hero / npc 3x4 walkcycles (16x16)
    this.load.spritesheet(TEX.townHero, `${base}/tilesets/town_rpg_pack/town_rpg_pack/graphics/hero.png`, { frameWidth: 16, frameHeight: 16 });
    this.load.spritesheet(TEX.townNpc, `${base}/tilesets/town_rpg_pack/town_rpg_pack/graphics/npc.png`, { frameWidth: 16, frameHeight: 16 });
    this.load.spritesheet(TEX.ttdChar, `${base}/tilesets/top_down_dungeon_and_character/top_down_character_v4.png`, { frameWidth: 16, frameHeight: 16 });

    // Hero base body (paperdoll) - new layered sprite system, 3 cols x 4 rows
    // Layout per character-image.md: row0=DOWN, row1=LEFT, row2=RIGHT, row3=UP; col0=step-L, col1=idle, col2=step-R
    this.load.spritesheet(TEX.bodyMale, `/assets/characters/body_male.png`, { frameWidth: 313, frameHeight: 418 });
    this.load.spritesheet(TEX.bodyFemale, `/assets/characters/body_female.png`, { frameWidth: 313, frameHeight: 418 });


    // v3 base body sheets (separate per animation)
    const charV3 = '/assets/characters/v3';
    this.load.spritesheet(TEX.bodyMaleV3Idle, `${charV3}/male/idle.png`, { frameWidth: 128, frameHeight: 128 });
    this.load.spritesheet(TEX.bodyMaleV3Walk, `${charV3}/male/walk.png`, { frameWidth: 96, frameHeight: 96 });
    this.load.spritesheet(TEX.bodyMaleV3Attack, `${charV3}/male/attack.png`, { frameWidth: 128, frameHeight: 128 });
    this.load.spritesheet(TEX.bodyMaleV3Hurt, `${charV3}/male/hurt.png`, { frameWidth: 128, frameHeight: 128 });
    this.load.spritesheet(TEX.bodyMaleV3Death, `${charV3}/male/death.png`, { frameWidth: 128, frameHeight: 128 });
    this.load.spritesheet(TEX.bodyFemaleV3Idle, `${charV3}/female/idle.png`, { frameWidth: 128, frameHeight: 128 });
    this.load.spritesheet(TEX.bodyFemaleV3Walk, `${charV3}/female/walk.png`, { frameWidth: 96, frameHeight: 96 });
    this.load.spritesheet(TEX.bodyFemaleV3Attack, `${charV3}/female/attack.png`, { frameWidth: 128, frameHeight: 128 });
    this.load.spritesheet(TEX.bodyFemaleV3Hurt, `${charV3}/female/hurt.png`, { frameWidth: 128, frameHeight: 128 });
    this.load.spritesheet(TEX.bodyFemaleV3Death, `${charV3}/female/death.png`, { frameWidth: 128, frameHeight: 128 });
    // Mockups (used as fallback world bg)
    this.load.image(TEX.mock01Lorencia, `${base}/mockups/01_lorencia.png`);
    this.load.image(TEX.mock02Noria, `${base}/mockups/02_noria.png`);
    this.load.image(TEX.mock03Devias, `${base}/mockups/03_devias.png`);
    this.load.image(TEX.mock04Dungeon, `${base}/mockups/04_dungeon.png`);
    this.load.image(TEX.mock05Atlans, `${base}/mockups/05_atlans.png`);
    this.load.image(TEX.mock06Tarkan, `${base}/mockups/06_tarkan.png`);

    // Desert tiles for Tarkan (each PNG is 256x256 — used as object/background)
    this.load.image(TEX.desertSand, `${base}/tilesets/desert_top_down/images/Tile_5.png`);
    this.load.image(TEX.desertEdge, `${base}/tilesets/desert_top_down/images/Tile_1.png`);
    this.load.image(TEX.desertCactus, `${base}/tilesets/desert_top_down/images/Object_5.png`);
    this.load.image(TEX.desertRock, `${base}/tilesets/desert_top_down/images/Object_8.png`);

    // Lorencia layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propOakTree, `/assets/props/oak_tree/prop.png`);
    this.load.image(TEX.propFountainGen, `/assets/props/fountain/prop.png`);
    this.load.spritesheet(TEX.propFountainIdle, `/assets/props/fountain/fountain-idle-sheet.png`, {
      frameWidth: 256, frameHeight: 256,
    });
    // Animated map-transition portal gate (single shared sheet for all directions)
    this.load.image(TEX.propPortalGate, `/assets/props/portal_gate/idle-1.png`);
    this.load.spritesheet(TEX.propPortalGateIdle, `/assets/props/portal_gate/portal-gate-idle-sheet.png`, {
      frameWidth: 256, frameHeight: 256,
    });
    this.load.image(TEX.propMarketStall, `/assets/props/market_stall/prop.png`);
    this.load.image(TEX.propHorseCart, `/assets/props/horse_cart/prop.png`);
    this.load.image(TEX.propWoodenBench, `/assets/props/wooden_bench/prop.png`);
    this.load.image(TEX.propStreetLantern, `/assets/props/street_lantern/prop.png`);
    this.load.image(TEX.propQuestBoard, `/assets/props/quest_board/prop.png`);
    // NOTE: Map base PNGs (lorencia-base, noria-base, ...) are loaded on demand
    // by maps/baseLoader.ts inside the active scene to avoid pulling every
    // map's heavy ground image at boot.

    // Noria layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propAncientOak, `/assets/props/ancient_oak/prop.png`);
    this.load.image(TEX.propGlowingMushroom, `/assets/props/glowing_mushroom/prop.png`);
    this.load.image(TEX.propElfLantern, `/assets/props/elf_lantern/prop.png`);
    this.load.image(TEX.propRuneStone, `/assets/props/rune_stone/prop.png`);

    // Devias layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propSnowyPine, `/assets/props/snowy_pine/prop.png`);
    this.load.image(TEX.propIcePillar, `/assets/props/ice_pillar/prop.png`);
    this.load.image(TEX.propHunterTent, `/assets/props/hunter_tent/prop.png`);
    this.load.image(TEX.propSnowCampfire, `/assets/props/snow_campfire/prop.png`);
    this.load.image(TEX.propRuinedPillar, `/assets/props/ruined_pillar/prop.png`);
    this.load.image(TEX.propIceLantern, `/assets/props/ice_lantern/prop.png`);

    // Atlans layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propFallenColumn, `/assets/props/fallen_column/prop.png`);
    this.load.image(TEX.propKelp, `/assets/props/kelp/prop.png`);
    this.load.image(TEX.propGiantClam, `/assets/props/giant_clam/prop.png`);
    this.load.image(TEX.propGlowJellyfish, `/assets/props/glow_jellyfish/prop.png`);
    this.load.image(TEX.propTritonStatue, `/assets/props/triton_statue/prop.png`);
    this.load.image(TEX.propSeaShrine, `/assets/props/sea_shrine/prop.png`);

    // Dungeon layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propWallTorch, `/assets/props/wall_torch/prop.png`);
    this.load.image(TEX.propStoneCoffin, `/assets/props/stone_coffin/prop.png`);
    this.load.image(TEX.propIronCage, `/assets/props/iron_cage/prop.png`);
    this.load.image(TEX.propSkullPile, `/assets/props/skull_pile/prop.png`);
    this.load.image(TEX.propBloodAltar, `/assets/props/blood_altar/prop.png`);
    this.load.image(TEX.propDarkThrone, `/assets/props/dark_throne/prop.png`);

    // Tarkan layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propDesertPalm, `/assets/props/desert_palm/prop.png`);
    this.load.image(TEX.propCaravanTent, `/assets/props/caravan_tent/prop.png`);
    this.load.image(TEX.propCamelSkeleton, `/assets/props/camel_skeleton/prop.png`);
    this.load.image(TEX.propSphinxStatue, `/assets/props/sphinx_statue/prop.png`);
    this.load.image(TEX.propSandstoneObelisk, `/assets/props/sandstone_obelisk/prop.png`);
    this.load.image(TEX.propPharaohSarcophagus, `/assets/props/pharaoh_sarcophagus/prop.png`);

    // Icarus layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propMarbleColumn, `/assets/props/marble_column/prop.png`);
    this.load.image(TEX.propAngelStatue, `/assets/props/angel_statue/prop.png`);
    this.load.image(TEX.propSkyLantern, `/assets/props/sky_lantern/prop.png`);
    this.load.image(TEX.propCrystalAltar, `/assets/props/crystal_altar/prop.png`);
    this.load.image(TEX.propWindPad, `/assets/props/wind_pad/prop.png`);
    this.load.image(TEX.propPegasusShrine, `/assets/props/pegasus_shrine/prop.png`);

    // Kalima layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propBloodAltarKalima, `/assets/props/blood_altar_kalima/prop.png`);
    this.load.image(TEX.propBonePillar, `/assets/props/bone_pillar/prop.png`);
    this.load.image(TEX.propHellBrazier, `/assets/props/hell_brazier/prop.png`);
    this.load.image(TEX.propKundunCoffin, `/assets/props/kundun_coffin/prop.png`);
    this.load.image(TEX.propKundunSymbol, `/assets/props/kundun_symbol/prop.png`);
    this.load.image(TEX.propHangingCorpse, `/assets/props/hanging_corpse/prop.png`);

    // Blood Castle layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propSealedDoor, `/assets/props/sealed_door/prop.png`);
    this.load.image(TEX.propHolyPedestal, `/assets/props/holy_pedestal/prop.png`);
    this.load.image(TEX.propGothicCandelabra, `/assets/props/gothic_candelabra/prop.png`);
    this.load.image(TEX.propBrokenAngel, `/assets/props/broken_angel/prop.png`);
    this.load.image(TEX.propBloodCoffin, `/assets/props/blood_coffin/prop.png`);
    this.load.image(TEX.propGothicBloodAltar, `/assets/props/gothic_blood_altar/prop.png`);

    // Devil Square layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propDsMagicPillar, `/assets/props/ds_magic_pillar/prop.png`);
    this.load.image(TEX.propDsDemonPortal, `/assets/props/ds_demon_portal/prop.png`);
    this.load.image(TEX.propDsDemonTorch, `/assets/props/ds_demon_torch/prop.png`);
    this.load.image(TEX.propDsBloodAltar, `/assets/props/ds_blood_altar/prop.png`);
    this.load.image(TEX.propDsCorpseCage, `/assets/props/ds_corpse_cage/prop.png`);
    this.load.image(TEX.propDsSpikePillar, `/assets/props/ds_spike_pillar/prop.png`);

    // Chaos Castle layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propCcChaosStatue, `/assets/props/cc_chaos_statue/prop.png`);
    this.load.image(TEX.propCcStoneBarrier, `/assets/props/cc_stone_barrier/prop.png`);
    this.load.image(TEX.propCcChaosAltar, `/assets/props/cc_chaos_altar/prop.png`);
    this.load.image(TEX.propCcChaosTorch, `/assets/props/cc_chaos_torch/prop.png`);
    this.load.image(TEX.propCcBrazier, `/assets/props/cc_brazier/prop.png`);

    // Illusion Temple layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propItCupIllusion, `/assets/props/it_cup_illusion/prop.png`);
    this.load.image(TEX.propItBuffPillar, `/assets/props/it_buff_pillar/prop.png`);
    this.load.image(TEX.propItArcaneLantern, `/assets/props/it_arcane_lantern/prop.png`);
    this.load.image(TEX.propItVanaStatue, `/assets/props/it_vana_statue/prop.png`);
    this.load.image(TEX.propItTeleportPad, `/assets/props/it_teleport_pad/prop.png`);

    // Crywolf layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propCwWolfStatue, `/assets/props/cw_wolf_statue/prop.png`);
    this.load.image(TEX.propCwWarDrum, `/assets/props/cw_war_drum/prop.png`);
    this.load.image(TEX.propCwWarBanner, `/assets/props/cw_war_banner/prop.png`);
    this.load.image(TEX.propCwFortressTower, `/assets/props/cw_fortress_tower/prop.png`);
    this.load.image(TEX.propCwFortressBrazier, `/assets/props/cw_fortress_brazier/prop.png`);
    this.load.image(TEX.propCwWolfThrone, `/assets/props/cw_wolf_throne/prop.png`);

    // Castle Siege layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propCsCastleGate, `/assets/props/cs_castle_gate/prop.png`);
    this.load.image(TEX.propCsCastleTower, `/assets/props/cs_castle_tower/prop.png`);
    this.load.image(TEX.propCsWarCannon, `/assets/props/cs_war_cannon/prop.png`);
    this.load.image(TEX.propCsWarTorch, `/assets/props/cs_war_torch/prop.png`);
    this.load.image(TEX.propCsDefenderFlag, `/assets/props/cs_defender_flag/prop.png`);
    this.load.image(TEX.propCsLorenciaCrown, `/assets/props/cs_lorencia_crown/prop.png`);

    // Golden Invasion layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propGiTreasureChest, `/assets/props/gi_treasure_chest/prop.png`);
    this.load.image(TEX.propGiGoldenObelisk, `/assets/props/gi_golden_obelisk/prop.png`);
    this.load.image(TEX.propGiGoldenShrine, `/assets/props/gi_golden_shrine/prop.png`);
    this.load.image(TEX.propGiGoldenLion, `/assets/props/gi_golden_lion/prop.png`);
    this.load.image(TEX.propGiGoldenBrazier, `/assets/props/gi_golden_brazier/prop.png`);

    // Hero Dungeon layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propHdCrystalPillar, `/assets/props/hd_crystal_pillar/prop.png`);
    this.load.image(TEX.propHdHeroPedestal, `/assets/props/hd_hero_pedestal/prop.png`);
    this.load.image(TEX.propHdDivineLantern, `/assets/props/hd_divine_lantern/prop.png`);
    this.load.image(TEX.propHdGuardianAltar, `/assets/props/hd_guardian_altar/prop.png`);
    this.load.image(TEX.propHdCrystalPool, `/assets/props/hd_crystal_pool/prop.png`);
    this.load.image(TEX.propHdAetheriaSymbol, `/assets/props/hd_aetheria_symbol/prop.png`);

    // Swamp of Calmness layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propScTwistedTree, `/assets/props/sc_twisted_tree/prop.png`);
    this.load.image(TEX.propScLilyPad, `/assets/props/sc_lily_pad/prop.png`);
    this.load.image(TEX.propScMushroomCluster, `/assets/props/sc_mushroom_cluster/prop.png`);
    this.load.image(TEX.propScPetrifiedWarrior, `/assets/props/sc_petrified_warrior/prop.png`);
    this.load.image(TEX.propScSnakeStatue, `/assets/props/sc_snake_statue/prop.png`);
    this.load.image(TEX.propScPoisonCauldron, `/assets/props/sc_poison_cauldron/prop.png`);
    this.load.image(TEX.propScSwampLantern, `/assets/props/sc_swamp_lantern/prop.png`);
    this.load.image(TEX.propScMossRock, `/assets/props/sc_moss_rock/prop.png`);

    // Lorencia Outskirt layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propOakSapling, `/assets/props/oak_sapling/prop.png`);
    this.load.image(TEX.propBerryBush, `/assets/props/berry_bush/prop.png`);
    this.load.image(TEX.propQuestSignpost, `/assets/props/quest_signpost/prop.png`);
    this.load.image(TEX.propMossyRocks, `/assets/props/mossy_rocks/prop.png`);
    this.load.image(TEX.propCattailReeds, `/assets/props/cattail_reeds/prop.png`);
    this.load.image(TEX.propStrawDummy, `/assets/props/straw_dummy/prop.png`);

    // Castle Siege layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propCsCastleGate, `/assets/props/cs_castle_gate/prop.png`);
    this.load.image(TEX.propCsCastleTower, `/assets/props/cs_castle_tower/prop.png`);
    this.load.image(TEX.propCsWarCannon, `/assets/props/cs_war_cannon/prop.png`);
    this.load.image(TEX.propCsWarTorch, `/assets/props/cs_war_torch/prop.png`);
    this.load.image(TEX.propCsDefenderFlag, `/assets/props/cs_defender_flag/prop.png`);
    this.load.image(TEX.propCsLorenciaCrown, `/assets/props/cs_lorencia_crown/prop.png`);

    // Golden Invasion layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propGiTreasureChest, `/assets/props/gi_treasure_chest/prop.png`);
    this.load.image(TEX.propGiGoldenObelisk, `/assets/props/gi_golden_obelisk/prop.png`);
    this.load.image(TEX.propGiGoldenShrine, `/assets/props/gi_golden_shrine/prop.png`);
    this.load.image(TEX.propGiGoldenLion, `/assets/props/gi_golden_lion/prop.png`);
    this.load.image(TEX.propGiGoldenBrazier, `/assets/props/gi_golden_brazier/prop.png`);

    // Hero Dungeon layered props (generated via generate2dsprite, transparent RGBA)
    this.load.image(TEX.propHdCrystalPillar, `/assets/props/hd_crystal_pillar/prop.png`);
    this.load.image(TEX.propHdHeroPedestal, `/assets/props/hd_hero_pedestal/prop.png`);
    this.load.image(TEX.propHdDivineLantern, `/assets/props/hd_divine_lantern/prop.png`);
    this.load.image(TEX.propHdGuardianAltar, `/assets/props/hd_guardian_altar/prop.png`);
    this.load.image(TEX.propHdCrystalPool, `/assets/props/hd_crystal_pool/prop.png`);
    this.load.image(TEX.propHdAetheriaSymbol, `/assets/props/hd_aetheria_symbol/prop.png`);

    // ----- Layered paperdoll atlases (per character-image.md §7.7) -----
    // Each atlas was packed by tools/sprites/sprite-pack.mjs; frame keys
    // follow `<category>/<basename>__<DIR>_<FRAME>` (DIR = DOWN/LEFT/RIGHT/UP,
    // FRAME = STEP_LEFT/IDLE/STEP_RIGHT/ATTACK_*/CAST_CHANNEL/HURT_KNOCKBACK).
    const atlasDir = '/atlases';
    this.load.atlas(TEX.atlasBase,   `${atlasDir}/base.png`,   `${atlasDir}/base.json`);
    this.load.atlas(TEX.atlasHair,   `${atlasDir}/hair.png`,   `${atlasDir}/hair.json`);
    this.load.atlas(TEX.atlasArmor,  `${atlasDir}/armor.png`,  `${atlasDir}/armor.json`);
    this.load.atlas(TEX.atlasWeapon, `${atlasDir}/weapon.png`, `${atlasDir}/weapon.json`);
    this.load.atlas(TEX.atlasShield, `${atlasDir}/shield.png`, `${atlasDir}/shield.json`);
    this.load.atlas(TEX.atlasFx,     `${atlasDir}/fx.png`,     `${atlasDir}/fx.json`);
    this.load.atlas(TEX.atlasMount,  `${atlasDir}/mount.png`,  `${atlasDir}/mount.json`);
    this.load.atlas(TEX.atlasEye,    `${atlasDir}/eye.png`,    `${atlasDir}/eye.json`);
    this.load.atlas(TEX.atlasIcons,  `${atlasDir}/icons.png`,  `${atlasDir}/icons.json`);

    // BGM for all 16 maps (CC0 / CC-BY)
    AudioManager.preloadAll(this);

    // High-detail per-NPC spritesheets (3x2 grid of 128x128 idle frames).
    for (const e of NPC_SPRITE_ENTRIES) {
      this.load.spritesheet(e.key, e.url, {
        frameWidth: NPC_SPRITE_FRAME_W,
        frameHeight: NPC_SPRITE_FRAME_H,
      });
    }

    // Per-monster spritesheets: move/attack are 4x4 grid of 96x96 frames
    // (rows = down/left/right/up); die is 4x1 grid of 128x128 frames.
    for (const m of MONSTER_SPRITE_ENTRIES) {
      this.load.spritesheet(m.moveKey, m.moveUrl, {
        frameWidth: MONSTER_MOVE_FRAME_W,
        frameHeight: MONSTER_MOVE_FRAME_H,
      });
      this.load.spritesheet(m.attackKey, m.attackUrl, {
        frameWidth: MONSTER_MOVE_FRAME_W,
        frameHeight: MONSTER_MOVE_FRAME_H,
      });
      this.load.spritesheet(m.dieKey, m.dieUrl, {
        frameWidth: MONSTER_DIE_FRAME_W,
        frameHeight: MONSTER_DIE_FRAME_H,
      });
    }

    // Item icons for inventory/drop rendering
    const itemIcons = new Set<string>();
    for (const item of (itemsData.items as { icon?: string }[])) {
      if (item.icon) itemIcons.add(item.icon);
    }
    for (const icon of itemIcons) {
      this.load.image(`item_${icon}`, `/assets/items/${icon}.png`);
    }

    // Pet companion spritesheets (2x2 grid, 256x256 per frame)
    const petCodes = ['fairy', 'demon', 'angel'];
    for (const code of petCodes) {
      this.load.spritesheet(`pet_${code}`, `/assets/pets/${code}/sheet-transparent.png`, {
        frameWidth: 256, frameHeight: 256,
      });
    }
  }

  create() {
    // --- Per-NPC idle anims for high-detail sheets (6 frames, 4 fps) ---
    for (const e of NPC_SPRITE_ENTRIES) {
      if (!this.textures.exists(e.key)) continue;
      const animKey = `${e.key}_idle`;
      if (this.anims.exists(animKey)) continue;
      this.anims.create({
        key: animKey,
        frames: this.anims.generateFrameNumbers(e.key, { start: 0, end: 5 }),
        frameRate: 4,
        repeat: -1,
      });
    }

    // --- Pet idle anims (4 frames from 2x2 sheet, 4 fps) ---
    for (const code of ['fairy', 'demon', 'angel']) {
      const key = `pet_${code}`;
      if (!this.textures.exists(key)) continue;
      const animKey = `${key}_idle`;
      if (this.anims.exists(animKey)) continue;
      this.anims.create({
        key: animKey,
        frames: this.anims.generateFrameNumbers(key, { start: 0, end: 3 }),
        frameRate: 4,
        repeat: -1,
      });
    }

    // --- Monster anims: walk_<dir> (4 fps, loop), attack_<dir> (10 fps, once),
    // die (8 fps, once). Walk/attack rows: down=0, left=1, right=2, up=3.
    const monsterDirs: MonsterDir[] = ['down', 'left', 'right', 'up'];
    for (const m of MONSTER_SPRITE_ENTRIES) {
      if (this.textures.exists(m.moveKey)) {
        for (const dir of monsterDirs) {
          const row = monsterDirRow(m.id, dir);
          const start = row * 4;
          const animKey = `${m.id}__walk_${dir}`;
          if (!this.anims.exists(animKey)) {
            this.anims.create({
              key: animKey,
              frames: this.anims.generateFrameNumbers(m.moveKey, { start, end: start + 3 }),
              frameRate: 8,
              repeat: -1,
            });
          }
          const idleKey = `${m.id}__idle_${dir}`;
          if (!this.anims.exists(idleKey)) {
            this.anims.create({
              key: idleKey,
              frames: [{ key: m.moveKey, frame: start }],
              frameRate: 1,
              repeat: -1,
            });
          }
        }
      }
      if (this.textures.exists(m.attackKey)) {
        for (const dir of monsterDirs) {
          const row = monsterDirRow(m.id, dir);
          const start = row * 4;
          const animKey = `${m.id}__attack_${dir}`;
          if (!this.anims.exists(animKey)) {
            this.anims.create({
              key: animKey,
              frames: this.anims.generateFrameNumbers(m.attackKey, { start, end: start + 3 }),
              frameRate: 12,
              repeat: 0,
            });
          }
        }
      }
      if (this.textures.exists(m.dieKey)) {
        const animKey = `${m.id}__die`;
        if (!this.anims.exists(animKey)) {
          this.anims.create({
            key: animKey,
            frames: this.anims.generateFrameNumbers(m.dieKey, { start: 0, end: 3 }),
            frameRate: 6,
            repeat: 0,
          });
        }
      }
    }

    // --- Real-asset character animations (hero.png / npc.png 3x4 walk cycle) ---
    // RPGMaker-style layout: rows = direction (0:down,1:left,2:right,3:up), cols = walk frame (0,1,2 with 1=idle)
    this.defineWalkAnims(TEX.townHero, 'hero');
    this.defineWalkAnims(TEX.townNpc, 'npc');

    // --- Paperdoll base body anims (per character-image.md contract) ---
    // 4-direction explicit (no flipX): DOWN=row0, LEFT=row1, RIGHT=row2, UP=row3
    this.defineBodyAnims(TEX.bodyMale, 'body_male');
    this.defineBodyAnims(TEX.bodyFemale, 'body_female');

    // --- v3 base body anims (separate sheets per animation) ---
    this.defineV3BodyAnims('body_male_v3', TEX.bodyMaleV3Idle, TEX.bodyMaleV3Walk, TEX.bodyMaleV3Attack, TEX.bodyMaleV3Hurt, TEX.bodyMaleV3Death);
    this.defineV3BodyAnims('body_female_v3', TEX.bodyFemaleV3Idle, TEX.bodyFemaleV3Walk, TEX.bodyFemaleV3Attack, TEX.bodyFemaleV3Hurt, TEX.bodyFemaleV3Death);

    // --- Atlas paperdoll anims (8x4 action grid) ---
    // Animation keys: `<atlasKey>__<basename>_<state>_<dir>` (e.g.
    // `atlas_base__body_unisex_walk_down`). Used by LayeredSprite.
    this.defineActionAtlasAnims(TEX.atlasBase, 'base/body_unisex');
    this.defineActionAtlasAnims(TEX.atlasArmor, 'armor/chest/plate_steel');
    this.defineActionAtlasAnims(TEX.atlasArmor, 'armor/chest/robe_purple');
    this.defineActionAtlasAnims(TEX.atlasArmor, 'armor/chest/leather_green');
    this.defineActionAtlasAnims(TEX.atlasWeapon, 'weapon/sword_iron');
    this.defineActionAtlasAnims(TEX.atlasWeapon, 'weapon/staff_wood_orb');
    this.defineActionAtlasAnims(TEX.atlasWeapon, 'weapon/bow_long');
    this.defineActionAtlasAnims(TEX.atlasShield, 'shield/round_sun');
    this.defineActionAtlasAnims(TEX.atlasEye, 'eye/open_default');
    this.defineActionAtlasAnims(TEX.atlasEye, 'eye/closed_default');
    // 3x4 walk-only layers (hair, helm)
    this.defineWalkAtlasAnims(TEX.atlasHair, 'hair/hair_short_brown');
    this.defineWalkAtlasAnims(TEX.atlasHair, 'hair/hair_long_silver');
    this.defineWalkAtlasAnims(TEX.atlasHair, 'hair/hair_ponytail_gold');
    this.defineWalkAtlasAnims(TEX.atlasArmor, 'armor/helm/iron_helm');
    this.defineWalkAtlasAnims(TEX.atlasArmor, 'armor/helm/wizard_hat');
    this.defineWalkAtlasAnims(TEX.atlasArmor, 'armor/helm/flower_crown');

    // Backgrounds (kept procedural for skill-fx scenes / fallback)
    this.makeBackgroundTex(TEX.bgTown, 0x2c3e50, 0x1a242f);
    this.makeBackgroundTex(TEX.bgField, 0x355c3d, 0x223a26);
    this.makeBackgroundTex(TEX.bgDungeon, 0x2b1c2c, 0x161018);

    // Class color reference (player tinting)
    this.makeCharTex(TEX.pWarrior, CLASS_COLORS.warrior);
    this.makeCharTex(TEX.pWizard, CLASS_COLORS.wizard);
    this.makeCharTex(TEX.pArcher, CLASS_COLORS.archer);

    // Monsters (still procedural — no sprite atlases for them yet)
    this.makeMonsterTex(TEX.monBullfighter, 0x8e44ad, 64);
    this.makeMonsterTex(TEX.monLich, 0x16a085, 56);
    this.makeMonsterTex(TEX.monGoblin, 0x6ab04c, 48);
    this.makeMonsterTex(TEX.monSkeleton, 0xecf0f1, 56);
    this.makeMonsterTex(TEX.monSpiderqueen, 0xc0392b, 80);

    // Skill / hit FX
    this.makeHitFx(TEX.hitFx);
    this.makeSlashFx(TEX.fxSlash);
    this.makeWhirlwindFx(TEX.fxWhirlwind);
    this.makeShieldFx(TEX.fxShield, 0xb0bec5);
    this.makeFireballFx(TEX.fxFireball);
    this.makeStormFx(TEX.fxStorm);
    this.makeShieldFx(TEX.fxManaShield, 0x42a5f5);
    this.makeArrowFx(TEX.fxArrow);
    this.makeArrowFx(TEX.fxArrowRain);
    this.makeHealFx(TEX.fxHeal);
    this.makeSparkFx(TEX.fxSpark);
    this.makeBasicMeleeFx(TEX.fxBasicMelee);
    this.makePotion(TEX.potion);
    this.makeGateTex(TEX.gateEast, 0x27ae60, 'Noria →');
    this.makeGateTex(TEX.gateWest, 0x8e44ad, '← Dungeon');
    this.makeGateTex(TEX.gateNorth, 0x3498db, '↑ Noria');
    this.makeGateTex(TEX.gateSouth, 0xe74c3c, '↓ Arena');

    // Lorencia procedural fallback tiles (kept in case a real-tile lookup misses)
    this.makeTile(TEX.lorenciaTile, 0x4a7c3f, 0x3d6b34);
    this.makeTile(TEX.lorenciaPath, 0x8d7b68, 0x7a6a59);
    this.makeTile(TEX.lorenciaWater, 0x2980b9, 0x1a5276);
    this.makeFountain(TEX.propFountain);
    this.makeDummy(TEX.propDummy);
    this.makeTree(TEX.propTree);
    this.makeBarrel(TEX.propBarrel);
    this.makeBench(TEX.propBench);
    this.makeSignShop(TEX.propSignShop);
    this.makeTorch(TEX.propTorch);
    this.makeAuctionBoard(TEX.propAuctionBoard);

    this.scene.start('Login');
  }

  // --- Real walk-cycle animation builder (3x4 layout) ---
  // hero.png and npc.png are 48x64 = 3 cols x 4 rows of 16x16 frames.
  // Row order = left(0), up(1), down(2). Row 3 is unused; right reuses left frames + flipX.
  private defineWalkAnims(textureKey: string, prefix: string) {
    if (!this.textures.exists(textureKey)) return;
    const dirs: Array<['down' | 'left' | 'up', number]> = [
      ['left', 0], ['up', 1], ['down', 2],
    ];
    for (const [name, row] of dirs) {
      const start = row * 3;
      this.anims.create({
        key: `${prefix}_walk_${name}`,
        frames: this.anims.generateFrameNumbers(textureKey, { frames: [start, start + 1, start + 2, start + 1] }),
        frameRate: 8,
        repeat: -1,
      });
      this.anims.create({
        key: `${prefix}_idle_${name}`,
        frames: [{ key: textureKey, frame: start + 1 }],
        frameRate: 1,
        repeat: -1,
      });
    }
    // Right reuses left frames (flipX handled in Player/NPC applyAnim)
    this.anims.create({
      key: `${prefix}_walk_right`,
      frames: this.anims.generateFrameNumbers(textureKey, { frames: [0, 1, 2, 1] }),
      frameRate: 8,
      repeat: -1,
    });
    this.anims.create({
      key: `${prefix}_idle_right`,
      frames: [{ key: textureKey, frame: 1 }],
      frameRate: 1,
      repeat: -1,
    });
  }

  // --- Paperdoll base body walk-cycle (3x4: 4 directions × 3 step frames) ---
  // Contract per character-image.md §1: row0=DOWN, row1=LEFT, row2=RIGHT, row3=UP
  // col0=STEP_LEFT, col1=IDLE, col2=STEP_RIGHT
  private defineBodyAnims(textureKey: string, prefix: string) {
    if (!this.textures.exists(textureKey)) return;
    const dirs: Array<['down' | 'left' | 'right' | 'up', number]> = [
      ['down', 0], ['left', 1], ['right', 2], ['up', 3],
    ];
    for (const [name, row] of dirs) {
      const start = row * 3;
      // Walk: step-L → idle → step-R → idle (loop) for natural left/right cadence
      this.anims.create({
        key: `${prefix}_walk_${name}`,
        frames: this.anims.generateFrameNumbers(textureKey, {
          frames: [start, start + 1, start + 2, start + 1],
        }),
        frameRate: 8,
        repeat: -1,
      });
      this.anims.create({
        key: `${prefix}_idle_${name}`,
        frames: [{ key: textureKey, frame: start + 1 }],
        frameRate: 1,
        repeat: -1,
      });
    }
  }

  // --- v3 multi-sheet body animation builder ---
  // idle/attack/hurt: 2x2 grid (128px), 1 frame per dir: 0=DOWN,1=LEFT,2=RIGHT,3=UP
  // walk: 4x4 grid (96px), 4 frames per dir row: row0=DOWN,row1=LEFT,row2=RIGHT,row3=UP
  // death: 2x3 grid (128px), 6 frames played as single sequence (direction-agnostic)
  private defineV3BodyAnims(
    prefix: string,
    idleKey: string, walkKey: string, attackKey: string, hurtKey: string, deathKey: string,
  ) {
    const dirs: Array<['down' | 'left' | 'right' | 'up', number]> = [
      ['down', 0], ['left', 1], ['right', 2], ['up', 3],
    ];

    // idle: 1 frame per direction (2x2 sheet)
    if (this.textures.exists(idleKey)) {
      for (const [dir, idx] of dirs) {
        const key = `${prefix}_idle_${dir}`;
        if (!this.anims.exists(key)) {
          this.anims.create({ key, frames: [{ key: idleKey, frame: idx }], frameRate: 1, repeat: -1 });
        }
      }
    }

    // walk: 4 frames per direction row (4x4 sheet)
    if (this.textures.exists(walkKey)) {
      for (const [dir, rowIdx] of dirs) {
        const start = rowIdx * 4;
        const key = `${prefix}_walk_${dir}`;
        if (!this.anims.exists(key)) {
          this.anims.create({
            key,
            frames: this.anims.generateFrameNumbers(walkKey, { frames: [start, start + 1, start + 2, start + 3, start + 2, start + 1] }),
            frameRate: 10,
            repeat: -1,
          });
        }
      }
    }

    // attack: 1 frame per direction (2x2 sheet)
    if (this.textures.exists(attackKey)) {
      for (const [dir, idx] of dirs) {
        const key = `${prefix}_attack_${dir}`;
        if (!this.anims.exists(key)) {
          this.anims.create({ key, frames: [{ key: attackKey, frame: idx }], frameRate: 6, repeat: 0 });
        }
      }
    }

    // hurt: 1 frame per direction (2x2 sheet)
    if (this.textures.exists(hurtKey)) {
      for (const [dir, idx] of dirs) {
        const key = `${prefix}_hurt_${dir}`;
        if (!this.anims.exists(key)) {
          this.anims.create({ key, frames: [{ key: hurtKey, frame: idx }], frameRate: 6, repeat: 0 });
        }
      }
    }

    // death: 6-frame sequence, direction-agnostic
    if (this.textures.exists(deathKey)) {
      const key = `${prefix}_death`;
      if (!this.anims.exists(key)) {
        this.anims.create({
          key,
          frames: this.anims.generateFrameNumbers(deathKey, { start: 0, end: 5 }),
          frameRate: 8,
          repeat: 0,
        });
      }
    }
  }

  // --- Atlas paperdoll animation builders (8x4 action grid) ---
  // Frame naming in atlas JSON: `{basename}__{DIR}_{ACTION}`
  // Anim key output: `{atlasKey}__{basename_slug}_{state}_{dir}`
  // e.g. frame `base/body_unisex__DOWN_IDLE` -> anim `atlas_base__base/body_unisex_idle_down`
  private defineActionAtlasAnims(atlasKey: string, basename: string) {
    if (!this.textures.exists(atlasKey)) return;
    const dirs: Array<['down' | 'left' | 'right' | 'up', string]> = [
      ['down', 'DOWN'], ['left', 'LEFT'], ['right', 'RIGHT'], ['up', 'UP'],
    ];
    const actions: Array<[string, string[]]> = [
      ['walk', ['STEP_LEFT', 'IDLE', 'STEP_RIGHT', 'IDLE']],
      ['idle', ['IDLE']],
      ['attack', ['ATTACK_WIND_UP', 'ATTACK_STRIKE', 'ATTACK_RECOVER']],
      ['cast', ['CAST_CHANNEL']],
      ['hurt', ['HURT_KNOCKBACK']],
    ];
    for (const [dirName, dirKey] of dirs) {
      for (const [state, frameKeys] of actions) {
        const frames = frameKeys
          .map((fk) => `${basename}__${dirKey}_${fk}`)
          .filter((fn) => this.textures.get(atlasKey).has(fn));
        if (frames.length === 0) continue;
        const animKey = `${atlasKey}__${basename}_${state}_${dirName}`;
        if (this.anims.exists(animKey)) continue;
        this.anims.create({
          key: animKey,
          frames: frames.map((f) => ({ key: atlasKey, frame: f })),
          frameRate: state === 'walk' ? 8 : state === 'attack' ? 10 : 6,
          repeat: (state === 'walk' || state === 'idle') ? -1 : 0,
        });
      }
    }
  }

  // Walk-only variant for layers that only have 3x4 walk frames (hair, helm).
  private defineWalkAtlasAnims(atlasKey: string, basename: string) {
    if (!this.textures.exists(atlasKey)) return;
    const dirs: Array<['down' | 'left' | 'right' | 'up', string]> = [
      ['down', 'DOWN'], ['left', 'LEFT'], ['right', 'RIGHT'], ['up', 'UP'],
    ];
    for (const [dirName, dirKey] of dirs) {
      const walkFrames = ['STEP_LEFT', 'IDLE', 'STEP_RIGHT', 'IDLE']
        .map((fk) => `${basename}__${dirKey}_${fk}`)
        .filter((fn) => this.textures.get(atlasKey).has(fn));
      if (walkFrames.length > 0) {
        const walkKey = `${atlasKey}__${basename}_walk_${dirName}`;
        if (!this.anims.exists(walkKey)) {
          this.anims.create({
            key: walkKey,
            frames: walkFrames.map((f) => ({ key: atlasKey, frame: f })),
            frameRate: 8,
            repeat: -1,
          });
        }
      }
      const idleFrame = `${basename}__${dirKey}_IDLE`;
      if (this.textures.get(atlasKey).has(idleFrame)) {
        const idleKey = `${atlasKey}__${basename}_idle_${dirName}`;
        if (!this.anims.exists(idleKey)) {
          this.anims.create({
            key: idleKey,
            frames: [{ key: atlasKey, frame: idleFrame }],
            frameRate: 1,
            repeat: -1,
          });
        }
      }
    }
  }

  // --- Background ---
  private makeBackgroundTex(key: string, top: number, bottom: number) {
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    const w = 1280, h = 720;
    g.fillGradientStyle(top, top, bottom, bottom, 1);
    g.fillRect(0, 0, w, h);
    g.fillStyle(0x000000, 0.25);
    g.fillRect(0, h * 0.78, w, h * 0.22);
    g.lineStyle(1, 0xffffff, 0.05);
    for (let x = 0; x < w; x += 40) g.strokeLineShape(new Phaser.Geom.Line(x, h * 0.78, x, h));
    g.generateTexture(key, w, h);
    g.destroy();
  }

  private makeCharTex(key: string, color: number) {
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    const w = 32, h = 48;
    g.fillStyle(color, 1).fillRoundedRect(4, 8, w - 8, h - 12, 4);
    g.fillStyle(0xfdd9b5, 1).fillCircle(w / 2, 8, 7);
    g.fillStyle(0x000000, 0.5).fillRect(4, 28, w - 8, 4);
    g.lineStyle(1, 0x000000, 0.7).strokeRect(0, 0, w, h);
    g.generateTexture(key, w, h);
    g.destroy();
  }

  private makeMonsterTex(key: string, color: number, size: number) {
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(color, 1).fillCircle(size / 2, size / 2, size / 2 - 2);
    g.fillStyle(0x000000, 1).fillCircle(size / 2 - 6, size / 2 - 4, 3).fillCircle(size / 2 + 6, size / 2 - 4, 3);
    g.lineStyle(2, 0x000000, 0.6).strokeCircle(size / 2, size / 2, size / 2 - 2);
    g.generateTexture(key, size, size);
    g.destroy();
  }

  // Soft radial glow helper: paints concentric translucent circles to fake a gradient.
  private paintGlow(g: Phaser.GameObjects.Graphics, cx: number, cy: number, radius: number, color: number, maxAlpha = 0.5, steps = 8) {
    for (let i = steps; i >= 1; i--) {
      const t = i / steps;
      g.fillStyle(color, maxAlpha * (1 - t) * (1 - t));
      g.fillCircle(cx, cy, radius * t);
    }
  }

  private makeHitFx(key: string) {
    const s = 64;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    this.paintGlow(g, s / 2, s / 2, 30, 0xffd166, 0.55, 8);
    g.fillStyle(0xffffff, 1).fillCircle(s / 2, s / 2, 12);
    g.fillStyle(0xffe082, 1).fillCircle(s / 2, s / 2, 6);
    g.generateTexture(key, s, s);
    g.destroy();
  }

  private makeSlashFx(key: string) {
    const w = 160, h = 80;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    // outer soft red glow
    g.lineStyle(18, 0xff1744, 0.18);
    g.beginPath(); g.arc(w / 2, h, 60, Phaser.Math.DegToRad(195), Phaser.Math.DegToRad(345), false); g.strokePath();
    // mid red
    g.lineStyle(10, 0xff5252, 0.7);
    g.beginPath(); g.arc(w / 2, h, 60, Phaser.Math.DegToRad(200), Phaser.Math.DegToRad(340), false); g.strokePath();
    // bright orange
    g.lineStyle(6, 0xffab40, 0.95);
    g.beginPath(); g.arc(w / 2, h, 56, Phaser.Math.DegToRad(205), Phaser.Math.DegToRad(335), false); g.strokePath();
    // hot core
    g.lineStyle(2, 0xffffff, 1);
    g.beginPath(); g.arc(w / 2, h, 56, Phaser.Math.DegToRad(210), Phaser.Math.DegToRad(330), false); g.strokePath();
    g.generateTexture(key, w, h);
    g.destroy();
  }

  private makeWhirlwindFx(key: string) {
    const s = 192;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    const cx = s / 2, cy = s / 2;
    this.paintGlow(g, cx, cy, 80, 0xff6f00, 0.18, 6);
    // 4 spiral arcs, layered
    for (let layer = 0; layer < 2; layer++) {
      const r = 70 - layer * 16;
      const w = 8 - layer * 3;
      const c = layer === 0 ? 0xffab40 : 0xffe082;
      g.lineStyle(w, c, 0.9 - layer * 0.2);
      for (let i = 0; i < 4; i++) {
        const a = (Math.PI / 2) * i + layer * 0.4;
        g.beginPath();
        g.arc(cx, cy, r, a, a + Math.PI / 2.4, false);
        g.strokePath();
      }
    }
    // inner core
    g.fillStyle(0xfff3a0, 0.85).fillCircle(cx, cy, 12);
    g.lineStyle(2, 0xffd180, 0.5).strokeCircle(cx, cy, 56);
    g.generateTexture(key, s, s);
    g.destroy();
  }

  private makeShieldFx(key: string, color: number) {
    const s = 144;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    const cx = s / 2, cy = s / 2;
    this.paintGlow(g, cx, cy, 64, color, 0.35, 8);
    g.fillStyle(color, 0.12).fillCircle(cx, cy, 60);
    g.lineStyle(5, color, 1).strokeCircle(cx, cy, 60);
    g.lineStyle(2, 0xffffff, 0.8).strokeCircle(cx, cy, 52);
    g.lineStyle(1, color, 0.55).strokeCircle(cx, cy, 44);
    // hex panels
    g.lineStyle(1.5, color, 0.7);
    for (let i = 0; i < 12; i++) {
      const a = (Math.PI / 6) * i;
      g.strokeLineShape(new Phaser.Geom.Line(
        cx + Math.cos(a) * 28, cy + Math.sin(a) * 28,
        cx + Math.cos(a) * 58, cy + Math.sin(a) * 58,
      ));
    }
    // small runes around perimeter
    g.fillStyle(0xffffff, 0.85);
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i + Math.PI / 6;
      g.fillCircle(cx + Math.cos(a) * 60, cy + Math.sin(a) * 60, 2.5);
    }
    g.generateTexture(key, s, s);
    g.destroy();
  }

  private makeFireballFx(key: string) {
    const s = 64;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    const cx = s / 2, cy = s / 2;
    this.paintGlow(g, cx, cy, 30, 0xff5722, 0.6, 10);
    g.fillStyle(0xff5722, 1).fillCircle(cx, cy, 18);
    g.fillStyle(0xff8c1a, 1).fillCircle(cx, cy, 13);
    g.fillStyle(0xffd166, 1).fillCircle(cx, cy, 8);
    g.fillStyle(0xffffff, 0.95).fillCircle(cx, cy, 4);
    g.generateTexture(key, s, s);
    g.destroy();
  }

  private makeStormFx(key: string) {
    const w = 160, h = 80;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    // soft purple glow halo behind cloud
    this.paintGlow(g, w / 2, h / 2, 60, 0x7e57c2, 0.25, 6);
    // dark cloud body
    g.fillStyle(0x263238, 1);
    g.fillCircle(40, 46, 26);
    g.fillCircle(80, 36, 32);
    g.fillCircle(120, 46, 26);
    g.fillCircle(60, 56, 22);
    g.fillCircle(100, 56, 22);
    g.fillStyle(0x455a64, 1);
    g.fillCircle(70, 40, 18);
    g.fillCircle(95, 40, 20);
    // electric highlights
    g.fillStyle(0xb39ddb, 0.9).fillCircle(60, 64, 5).fillCircle(95, 66, 4).fillCircle(78, 60, 3);
    g.fillStyle(0xe1bee7, 0.7).fillCircle(60, 64, 2);
    g.generateTexture(key, w, h);
    g.destroy();
  }

  private makeArrowFx(key: string) {
    const w = 64, h = 16;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    // soft glow trail behind shaft
    g.fillStyle(0xfff59d, 0.25).fillRect(0, 5, 56, 6);
    g.fillStyle(0xfff59d, 0.5).fillRect(8, 6, 44, 4);
    // shaft
    g.fillStyle(0x6d4c41, 1).fillRect(6, 7, 44, 2);
    g.fillStyle(0x8d6e63, 1).fillRect(6, 6, 44, 1);
    // arrowhead
    g.fillStyle(0xeceff1, 1);
    g.beginPath(); g.moveTo(48, 2); g.lineTo(64, 8); g.lineTo(48, 14); g.closePath(); g.fillPath();
    g.fillStyle(0xb0bec5, 1);
    g.beginPath(); g.moveTo(48, 8); g.lineTo(60, 8); g.lineTo(48, 14); g.closePath(); g.fillPath();
    // fletching
    g.fillStyle(0xc62828, 1);
    g.beginPath(); g.moveTo(0, 2); g.lineTo(10, 8); g.lineTo(0, 14); g.closePath(); g.fillPath();
    g.fillStyle(0xef5350, 1).fillRect(2, 6, 6, 4);
    g.generateTexture(key, w, h);
    g.destroy();
  }

  private makeHealFx(key: string) {
    const s = 96;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    const cx = s / 2, cy = s / 2;
    this.paintGlow(g, cx, cy, 44, 0x66bb6a, 0.45, 8);
    g.fillStyle(0xa5d6a7, 0.4).fillCircle(cx, cy, 40);
    // plus sign with bevel
    g.fillStyle(0x2e7d32, 1).fillRoundedRect(cx - 7, 16, 14, 64, 4);
    g.fillStyle(0x2e7d32, 1).fillRoundedRect(16, cy - 7, 64, 14, 4);
    g.fillStyle(0x66bb6a, 1).fillRoundedRect(cx - 5, 18, 10, 60, 3);
    g.fillStyle(0x66bb6a, 1).fillRoundedRect(18, cy - 5, 60, 10, 3);
    g.fillStyle(0xc8e6c9, 1).fillRoundedRect(cx - 2, 20, 4, 56, 2);
    g.fillStyle(0xc8e6c9, 1).fillRoundedRect(20, cy - 2, 56, 4, 2);
    g.lineStyle(2, 0xc8e6c9, 0.85).strokeCircle(cx, cy, 44);
    g.generateTexture(key, s, s);
    g.destroy();
  }

  private makeSparkFx(key: string) {
    const s = 32;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    const cx = s / 2, cy = s / 2;
    this.paintGlow(g, cx, cy, 14, 0x6ec1ff, 0.6, 6);
    g.fillStyle(0xffffff, 1).fillCircle(cx, cy, 6);
    g.fillStyle(0x6ec1ff, 1).fillCircle(cx, cy, 4);
    g.fillStyle(0xffffff, 0.9).fillCircle(cx, cy, 2);
    g.generateTexture(key, s, s);
    g.destroy();
  }

  private makeBasicMeleeFx(key: string) {
    const w = 96, h = 48;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.lineStyle(12, 0xffffff, 0.2);
    g.beginPath(); g.arc(w / 2, h, 36, Phaser.Math.DegToRad(200), Phaser.Math.DegToRad(340), false); g.strokePath();
    g.lineStyle(6, 0xffffff, 0.85);
    g.beginPath(); g.arc(w / 2, h, 36, Phaser.Math.DegToRad(205), Phaser.Math.DegToRad(335), false); g.strokePath();
    g.lineStyle(2, 0xfff3a0, 1);
    g.beginPath(); g.arc(w / 2, h, 36, Phaser.Math.DegToRad(210), Phaser.Math.DegToRad(330), false); g.strokePath();
    g.generateTexture(key, w, h);
    g.destroy();
  }

  private makePotion(key: string) {
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0xb71c1c, 1).fillRoundedRect(4, 6, 20, 22, 6);
    g.fillStyle(0x424242, 1).fillRect(8, 2, 12, 6);
    g.generateTexture(key, 28, 32);
    g.destroy();
  }

  private makeGateTex(key: string, glowColor: number, _label: string) {
    const w = 96, h = 160;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0x5d4037, 1);
    g.fillRect(0, 20, 18, h - 20);
    g.fillRect(w - 18, 20, 18, h - 20);
    g.fillStyle(0x6d4c41, 1);
    g.fillRect(0, 20, w, 20);
    g.fillStyle(0x4e342e, 1);
    g.fillCircle(w / 2, 20, w / 2);
    g.fillStyle(glowColor, 0.35);
    g.fillRect(18, 40, w - 36, h - 40);
    g.lineStyle(1, glowColor, 0.6);
    for (let y = 50; y < h; y += 14) {
      g.strokeLineShape(new Phaser.Geom.Line(20, y, w - 20, y));
    }
    g.lineStyle(3, glowColor, 1);
    g.strokeRect(18, 40, w - 36, h - 40);
    g.lineStyle(1, 0x3e2723, 0.5);
    for (let y = 30; y < h; y += 20) {
      g.strokeLineShape(new Phaser.Geom.Line(0, y, 18, y));
      g.strokeLineShape(new Phaser.Geom.Line(w - 18, y, w, y));
    }
    g.generateTexture(key, w, h);
    g.destroy();
  }

  private makeTile(key: string, base: number, accent: number) {
    const s = 32;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(base, 1).fillRect(0, 0, s, s);
    g.fillStyle(accent, 0.5);
    g.fillRect(4, 4, 8, 6);
    g.fillRect(18, 20, 10, 8);
    g.fillRect(8, 22, 6, 6);
    g.lineStyle(1, 0x000000, 0.08).strokeRect(0, 0, s, s);
    g.generateTexture(key, s, s);
    g.destroy();
  }

  private makeFountain(key: string) {
    const s = 96;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0x5d6d7e, 1);
    g.fillCircle(s / 2, s / 2, 40);
    g.fillStyle(0x2980b9, 0.7);
    g.fillCircle(s / 2, s / 2, 34);
    g.fillStyle(0x7f8c8d, 1);
    g.fillCircle(s / 2, s / 2, 10);
    g.fillStyle(0x85c1e9, 0.6);
    g.fillCircle(s / 2, s / 2 - 14, 6);
    g.fillCircle(s / 2 - 10, s / 2 - 8, 4);
    g.fillCircle(s / 2 + 10, s / 2 - 8, 4);
    g.lineStyle(2, 0x34495e, 0.8).strokeCircle(s / 2, s / 2, 40);
    g.generateTexture(key, s, s);
    g.destroy();
  }

  private makeDummy(key: string) {
    const w = 32, h = 48;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0x6d4c41, 1).fillRect(13, 16, 6, 32);
    g.fillStyle(0x795548, 1).fillRect(4, 20, 24, 5);
    g.fillStyle(0xd7ccc8, 1).fillCircle(w / 2, 12, 8);
    g.fillStyle(0x000000, 1).fillCircle(13, 10, 2).fillCircle(19, 10, 2);
    g.lineStyle(1, 0x4e342e, 0.7).strokeRect(0, 0, w, h);
    g.generateTexture(key, w, h);
    g.destroy();
  }

  private makeTree(key: string) {
    const s = 64;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0x5d4037, 1).fillRect(26, 36, 12, 28);
    g.fillStyle(0x2e7d32, 1).fillCircle(s / 2, 24, 22);
    g.fillStyle(0x388e3c, 0.7).fillCircle(s / 2 - 8, 20, 14);
    g.fillStyle(0x43a047, 0.5).fillCircle(s / 2 + 8, 18, 12);
    g.generateTexture(key, s, s);
    g.destroy();
  }

  private makeBarrel(key: string) {
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0x6d4c41, 1).fillRoundedRect(4, 4, 24, 24, 4);
    g.fillStyle(0x4e342e, 1).fillRect(4, 10, 24, 3).fillRect(4, 19, 24, 3);
    g.lineStyle(1, 0x3e2723, 0.6).strokeRoundedRect(4, 4, 24, 24, 4);
    g.generateTexture(key, 32, 32);
    g.destroy();
  }

  private makeBench(key: string) {
    const w = 64, h = 32;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0x795548, 1).fillRect(4, 10, w - 8, 8);
    g.fillStyle(0x5d4037, 1);
    g.fillRect(8, 18, 4, 10);
    g.fillRect(w - 12, 18, 4, 10);
    g.fillStyle(0x6d4c41, 1).fillRect(4, 4, w - 8, 6);
    g.generateTexture(key, w, h);
    g.destroy();
  }

  private makeSignShop(key: string) {
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0x5d4037, 1).fillRect(13, 12, 6, 20);
    g.fillStyle(0xf9e79f, 1).fillRoundedRect(4, 2, 24, 14, 2);
    g.lineStyle(1, 0x7d6608, 0.8).strokeRoundedRect(4, 2, 24, 14, 2);
    g.generateTexture(key, 32, 32);
    g.destroy();
  }

  private makeTorch(key: string) {
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0x5d4037, 1).fillRect(13, 10, 6, 22);
    g.fillStyle(0xf39c12, 0.9).fillCircle(16, 8, 6);
    g.fillStyle(0xe74c3c, 0.7).fillCircle(16, 6, 4);
    g.generateTexture(key, 32, 32);
    g.destroy();
  }

  private makeAuctionBoard(key: string) {
    const w = 48, h = 48;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0x5d4037, 1);
    g.fillRect(6, 16, 5, 32);
    g.fillRect(w - 11, 16, 5, 32);
    g.fillStyle(0x8d6e63, 1).fillRect(4, 4, w - 8, 24);
    g.fillStyle(0xfff9c4, 0.8);
    g.fillRect(8, 8, 10, 6);
    g.fillRect(20, 8, 10, 6);
    g.fillRect(8, 16, 10, 6);
    g.fillRect(20, 16, 10, 6);
    g.lineStyle(1, 0x3e2723, 0.6).strokeRect(4, 4, w - 8, 24);
    g.generateTexture(key, w, h);
    g.destroy();
  }
}
